/**
 * Image Format Configuration
 * Centralizes all format definitions for orientation, size, and Gelato mapping
 */

import { PRINT_DIMENSIONS_PX } from '../../supabase/functions/_shared/print-quality';

export { PRINT_DIMENSIONS_PX };

export type Orientation = 'portrait' | 'landscape' | 'square';
export type FormatSize = 'small' | 'medium' | 'large';

export interface OrientationOption {
  id: Orientation;
  label: string;
  description: string;
}

export interface SizeOption {
  id: FormatSize;
  dimensions: string;
  label: string;
  description: string;
  aspectRatio: string;
  fluxRatio: string;
  nanoBananaRatio: string; // Ratio envoyé à Nano Banana (ratio exact Gelato quand supporté)
  priceCents: number;
  priceDisplay: string;
  woodFramePriceCents: number;
  woodFramePriceDisplay: string;
}

export interface FormatConfig {
  small: SizeOption;
  medium: SizeOption;
  large: SizeOption;
}

// Orientation options for UI
export const ORIENTATIONS: OrientationOption[] = [
  {
    id: 'portrait',
    label: 'Portrait',
    description: 'Format vertical, idéal pour les portraits et scènes verticales',
  },
  {
    id: 'landscape',
    label: 'Paysage',
    description: 'Format horizontal, parfait pour les panoramas et paysages',
  },
  {
    id: 'square',
    label: 'Carré',
    description: 'Format équilibré, moderne et polyvalent',
  },
];

// Size configurations per orientation
export const FORMAT_CONFIG: Record<Orientation, FormatConfig> = {
  portrait: {
    small: {
      id: 'small',
      dimensions: '15x20',
      label: '15×20 cm',
      description: 'Format compact',
      aspectRatio: '3:4',
      fluxRatio: '3:4',
      nanoBananaRatio: '3:4',
      priceCents: 2999,
      priceDisplay: '29,99 €',
      woodFramePriceCents: 5999,
      woodFramePriceDisplay: '59,99 €',
    },
    medium: {
      id: 'medium',
      dimensions: '30x40',
      label: '30×40 cm',
      description: 'Format standard',
      aspectRatio: '3:4',
      fluxRatio: '3:4',
      nanoBananaRatio: '3:4',
      priceCents: 3999,
      priceDisplay: '39,99 €',
      woodFramePriceCents: 7999,
      woodFramePriceDisplay: '79,99 €',
    },
    large: {
      id: 'large',
      dimensions: '45x60',
      label: '45×60 cm',
      description: 'Grand format',
      aspectRatio: '3:4',
      fluxRatio: '3:4',
      nanoBananaRatio: '3:4',
      priceCents: 4999,
      priceDisplay: '49,99 €',
      woodFramePriceCents: 12999,
      woodFramePriceDisplay: '129,99 €',
    },
  },
  landscape: {
    small: {
      id: 'small',
      dimensions: '20x15',
      label: '20×15 cm',
      description: 'Format compact',
      aspectRatio: '4:3',
      fluxRatio: '4:3',
      nanoBananaRatio: '4:3',
      priceCents: 2999,
      priceDisplay: '29,99 €',
      woodFramePriceCents: 5999,
      woodFramePriceDisplay: '59,99 €',
    },
    medium: {
      id: 'medium',
      dimensions: '40x30',
      label: '40×30 cm',
      description: 'Format standard',
      aspectRatio: '4:3',
      fluxRatio: '4:3',
      nanoBananaRatio: '4:3',
      priceCents: 3999,
      priceDisplay: '39,99 €',
      woodFramePriceCents: 7999,
      woodFramePriceDisplay: '79,99 €',
    },
    large: {
      id: 'large',
      dimensions: '60x45',
      label: '60×45 cm',
      description: 'Grand format',
      aspectRatio: '4:3',
      fluxRatio: '4:3',
      nanoBananaRatio: '4:3',
      priceCents: 4999,
      priceDisplay: '49,99 €',
      woodFramePriceCents: 12999,
      woodFramePriceDisplay: '129,99 €',
    },
  },
  square: {
    small: {
      id: 'small',
      dimensions: '30x30',
      label: '30×30 cm',
      description: 'Format compact',
      aspectRatio: '1:1',
      fluxRatio: '1:1',
      nanoBananaRatio: '1:1',
      priceCents: 3499,
      priceDisplay: '34,99 €',
      woodFramePriceCents: 7999,
      woodFramePriceDisplay: '79,99 €',
    },
    medium: {
      id: 'medium',
      dimensions: '40x40',
      label: '40×40 cm',
      description: 'Format standard',
      aspectRatio: '1:1',
      fluxRatio: '1:1',
      nanoBananaRatio: '1:1',
      priceCents: 4499,
      priceDisplay: '44,99 €',
      woodFramePriceCents: 9999,
      woodFramePriceDisplay: '99,99 €',
    },
    large: {
      id: 'large',
      dimensions: '50x50',
      label: '50×50 cm',
      description: 'Grand format',
      aspectRatio: '1:1',
      fluxRatio: '1:1',
      nanoBananaRatio: '1:1',
      priceCents: 5499,
      priceDisplay: '54,99 €',
      woodFramePriceCents: 11999,
      woodFramePriceDisplay: '119,99 €',
    },
  },
};

// Gelato product UID mapping for flat (unframed) posters
// Format: `${orientation}_${dimensions}` -> Gelato product UID
// Note: Framed posters use buildGelatoProductUid() from gelatoProductBuilder.ts
export const GELATO_PRODUCT_MAPPING: Record<string, string> = {
  // Portrait formats (ver = vertical)
  'portrait_15x20': 'flat_150x200-mm-6x8-inch_200-gsm-80lb-uncoated_4-0_ver',
  'portrait_30x40': 'flat_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver',
  'portrait_45x60': 'flat_450x600-mm-18x24-inch_200-gsm-80lb-uncoated_4-0_ver',
  // Landscape formats (hor = horizontal) — Gelato expects dimensions in portrait order (small x large)
  'landscape_20x15': 'flat_150x200-mm-6x8-inch_200-gsm-80lb-uncoated_4-0_hor',
  'landscape_40x30': 'flat_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_hor',
  'landscape_60x45': 'flat_450x600-mm-18x24-inch_200-gsm-80lb-uncoated_4-0_hor',
  // Square formats (ver = vertical)
  'square_30x30': 'flat_300x300-mm-12x12-inch_200-gsm-80lb-uncoated_4-0_ver',
  'square_40x40': 'flat_400x400-mm-16x16-inch_200-gsm-80lb-uncoated_4-0_ver',
  'square_50x50': 'flat_500x500-mm-20x20-inch_200-gsm-80lb-uncoated_4-0_ver',
};

// Helper function to get format key for Gelato mapping
export function getFormatKey(orientation: Orientation, dimensions: string): string {
  return `${orientation}_${dimensions}`;
}

// Helper function to get Gelato product UID
export function getGelatoProductUid(orientation: Orientation, dimensions: string): string | undefined {
  const key = getFormatKey(orientation, dimensions);
  return GELATO_PRODUCT_MAPPING[key];
}

// Helper function to get size options for an orientation
export function getSizeOptionsForOrientation(orientation: Orientation): SizeOption[] {
  const config = FORMAT_CONFIG[orientation];
  return [config.small, config.medium, config.large];
}

// Helper function to get Flux aspect ratio from orientation and size
export function getFluxRatio(orientation: Orientation, size: FormatSize): string {
  return FORMAT_CONFIG[orientation][size].fluxRatio;
}

// Helper function to get Nano Banana aspect ratio from orientation and size
export function getNanoBananaRatio(orientation: Orientation, size: FormatSize): string {
  return FORMAT_CONFIG[orientation][size].nanoBananaRatio;
}

export function canReuseGenerationForSize(
  orientation: Orientation,
  generatedSize: FormatSize,
  selectedSize: FormatSize,
): boolean {
  return getNanoBananaRatio(orientation, generatedSize)
    === getNanoBananaRatio(orientation, selectedSize);
}

// Helper function to get dimensions from orientation and size
export function getDimensions(orientation: Orientation, size: FormatSize): string {
  return FORMAT_CONFIG[orientation][size].dimensions;
}

// Helper function to get print dimensions in pixels for a given orientation + dimensions key
export function getPrintDimensions(orientation: Orientation, dimensions: string): { width: number; height: number } | undefined {
  const key = getFormatKey(orientation, dimensions);
  return PRINT_DIMENSIONS_PX[key];
}

// Helper to get the aspect ratio for an orientation (all sizes share the same ratio)
export function getOrientationRatio(orientation: Orientation): string {
  return FORMAT_CONFIG[orientation].medium.nanoBananaRatio;
}

// Helper to get default dimensions for generation (ratio is the same for all sizes)
export function getDefaultDimensions(orientation: Orientation): string {
  return FORMAT_CONFIG[orientation].medium.dimensions;
}

// Legacy mapping for backward compatibility (old format "20x25" -> new format)
// This helps during migration
export const LEGACY_FORMAT_MAPPING: Record<string, { orientation: Orientation; size: FormatSize }> = {
  '15x20': { orientation: 'portrait', size: 'small' },
  '30x40': { orientation: 'portrait', size: 'medium' },
  '60x80': { orientation: 'portrait', size: 'large' },
};
